import { Media } from "./Media";

/**
 * Model intern del DISSENYADOR D'EXERCICIS (Fase 6).
 * Veure taskgen-srv/docs/Disseny-DissenyadorExercicis.md.
 *
 * PROPOSTA — pendent de validar abans de muntar la UI.
 *
 * Aquest model és la font de veritat: l'editor el produeix, el reproductor
 * intern el consumeix, i els exports (Moodle XML / GIFT) són funcions pures
 * `Exercise -> arxiu`. Es persisteix al backend (col·lecció `Exercise`) com a
 * { _id, userId, name, type, payload, defaultgrade, penalty }.
 *
 * Decisió clau: un exercici és O BÉ un Cloze (un sol text amb buits incrustats,
 * → 1 entrada Moodle) O BÉ una llista de preguntes independents (→ N entrades).
 * No es barregen Cloze i preguntes dins el mateix exercici (§4.1 del doc). La
 * llista de preguntes independents SÍ que pot barrejar subtipus.
 */

// `type` de l'exercici (top-level). Substitueix la llista de 7 tipus-fulla de
// l'stub: ara el subtipus de cada pregunta viu dins `payload.questions[].type`.
export type ExerciseType = 'cloze' | 'questions';

export interface Exercise {
    _id?: string;
    name: string;
    type: ExerciseType;
    payload: ClozePayload | QuestionsPayload;
    defaultgrade: number;   // punts per pregunta (default Moodle: 1)
    penalty: number;        // sanció intent incorrecte en mode adaptatiu (default: 0.3333)
}

// ───────────────────────── Cloze ─────────────────────────
//
// Un text enriquit amb buits incrustats. Cada buit es representa dins `text`
// amb un marcador `{{gap:ID}}` i les seves dades es guarden a `gaps` per `id`.
// Els tres subtipus poden conviure dins el mateix Cloze.

export type ClozeGapSubtype = 'gapfill' | 'multichoice' | 'truefalse';

export interface ClozeGap {
    id: string;                 // id estable, referenciat pel marcador del text
    subtype: ClozeGapSubtype;
    clue?: string;              // pista opcional

    // gapfill (SHORTANSWER): la paraula seleccionada és la correcta + alternatives
    answer?: string;
    alternatives?: string[];
    caseSensitive?: boolean;

    // multichoice inline: una opció correcta + distractors
    options?: ChoiceOption[];

    // truefalse inline
    correct?: boolean;
}

export interface ClozePayload {
    text: string;               // HTML ric amb marcadors {{gap:ID}}
    gaps: ClozeGap[];
}

// ──────────────── Preguntes independents ────────────────

export type QuestionType =
    | 'shortanswer' | 'essay' | 'truefalse' | 'multichoice' | 'matching' | 'ordering';

export interface BaseQuestion {
    id: string;
    type: QuestionType;
    statement: string;          // enunciat (HTML ric)
    media?: Media[];            // imatge/àudio/vídeo a l'enunciat (§4.4)
    defaultgrade?: number;      // override opcional del valor de l'exercici
    penalty?: number;           // override opcional
}

export interface ChoiceOption {
    text: string;
    correct: boolean;
    weight?: number;            // pes (per a parcialment correctes)
}

export interface ShortAnswerQuestion extends BaseQuestion {
    type: 'shortanswer';
    answers: { text: string; weight: number }[];   // respostes acceptades amb pes
    caseSensitive?: boolean;
}

export interface EssayQuestion extends BaseQuestion {
    type: 'essay';              // resposta oberta, sense auto-correcció
}

export interface TrueFalseQuestion extends BaseQuestion {
    type: 'truefalse';
    correct: boolean;
}

export interface MultiChoiceQuestion extends BaseQuestion {
    type: 'multichoice';
    multiple: boolean;          // una o més respostes correctes
    options: ChoiceOption[];
}

export interface MatchingQuestion extends BaseQuestion {
    type: 'matching';
    pairs: { left: string; right: string }[];       // dues columnes
}

export interface OrderingQuestion extends BaseQuestion {
    type: 'ordering';
    items: string[];            // en l'ordre correcte (es desordena a la plataforma)
}

export type Question =
    | ShortAnswerQuestion | EssayQuestion | TrueFalseQuestion
    | MultiChoiceQuestion | MatchingQuestion | OrderingQuestion;

export interface QuestionsPayload {
    questions: Question[];
}

// Constants compartides (UI, validació backend, exports).
export const EXERCISE_TYPES: ExerciseType[] = ['cloze', 'questions'];
export const QUESTION_TYPES: QuestionType[] = [
    'shortanswer', 'essay', 'truefalse', 'multichoice', 'matching', 'ordering'
];
export const CLOZE_GAP_SUBTYPES: ClozeGapSubtype[] = ['gapfill', 'multichoice', 'truefalse'];

export const DEFAULT_GRADE = 1;
export const DEFAULT_PENALTY = 0.3333;
